import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { User, UserFormProps } from './User.d'
import { Card } from 'primereact/card'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import PageHeader from '../../../layout/page/PageHeader'
import { Language } from '../../../../features/security/security.slice'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../../common/forms/fields/TextField'
import ImageField from '../../../common/forms/fields/ImageField'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import SelectField from '../../../common/forms/fields/SelectField'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import RenderTranslatedFields from '../../../common/forms/fields/translated-fields/RenderTranslatedFields'
import DateField from '../../../common/forms/fields/DateField'
import FormActions from '../../../common/forms/FormActions'
import {
    getGroupTranslation,
    getTranslation,
} from '../../../common/utility/helpers'
import CheckboxField from '../../../common/forms/fields/CheckboxField'
import { ROLES } from '../../../../routeConfig'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'
import { HintMessage } from '../../../common/messages/Messages'
import ImageReccomendedDimentionsMessage from '../../../common/messages/ImageReccomendedDimentionsMessage'

const UserForm: FC<UserFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const isViewOrEdit = id !== null
    const pageCode = isViewOrEdit ? 'USER_' + id : 'TMPSET'
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)

    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()

    const emptyUser: User = {
        titles_id: null,
        first_name: '',
        middle_names: '',
        last_name: '',
        date_of_birth: '',
        username: '',
        email: '',
        media_store_id: null,
        user_image: '',
        remove_user_image: false,
        roles: [],
    }

    const [userDialog, setUserDialog] = useState(true)
    const [user, setUser] = useState<User>(emptyUser)
    const [recordName, setRecordName] = useState('')
    const [titles, setTitles] = useState<[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [allowRemoveImage, setAllowRemoveImage] = useState(false)
    const [previewSrc, setPreviewSrc] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    let schema = {
        username: Yup.string().trim().required('Enter a unique username'),
        email: Yup.string().trim().required('Enter your email address'),
        first_name: Yup.string().trim().required('Required'),
        last_name: Yup.string().trim().required('Required'),
        roles: Yup.array()
            .min(1, 'You must select at least one security role')
            .required(),
    }

    let _initialValues = {
        ...user,
        first_name: pageCode + '_FIRST_NAME',
        middle_names: pageCode + '_MIDDLE_NAMES',
        last_name: pageCode + '_LAST_NAME',
    }

    Object.values(initData.languages.data).map((lang: Language) => {
        schema[`first_name_${lang.id}`] = Yup.string()
            .trim()
            .required(`The First Name (${lang.name}) field is required`)
        schema[`last_name_${lang.id}`] = Yup.string()
            .trim()
            .required(`The Last Name (${lang.name}) field is required`)
        _initialValues[`first_name_${lang.id}`] = ''
        _initialValues[`last_name_${lang.id}`] = ''
        return true
    })

    const [initialValues, setInitialValues] = useState(_initialValues)

    const UserFormSchema = Yup.object().shape(schema)

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/administration/users/' + id
            : '/administration/users/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })

            if (response.data?.success === true) {
                setTitles(response.data.data.titles)

                if (response.data.data.user) {
                    setUser({
                        ...response.data.data.user,
                        roles: response.data.data.roles,
                    })
                    setRecordName(
                        getTranslation(
                            response.data.data.translations,
                            response.data.data.user.first_name,
                            initData.languages.default.locale
                        )
                    )
                    setInitialValues((prevState) => {
                        return {
                            ...prevState,
                            ...response.data.data.user,
                            roles: response.data.data.roles,
                        }
                    })
                    Object.keys(initData.languages.data).map((locale) => {
                        setUser((prevState) => {
                            const lang: Language =
                                initData.languages.data[locale]

                            const groupTranslations = getGroupTranslation(
                                response.data.data.translations,
                                [
                                    {
                                        key: 'first_name',
                                        code: response.data.data.user
                                            .first_name,
                                    },
                                    {
                                        key: 'middle_names',
                                        code: response.data.data.user
                                            .middle_names,
                                    },
                                    {
                                        key: 'last_name',
                                        code: response.data.data.user.last_name,
                                    },
                                ],
                                locale
                            )

                            setInitialValues((prevState) => {
                                return {
                                    ...prevState,
                                    [`first_name_${lang.id}`]:
                                        groupTranslations['first_name'],
                                    [`last_name_${lang.id}`]:
                                        groupTranslations['last_name'],
                                }
                            })

                            return {
                                ...prevState,
                                [`first_name_${lang.id}`]:
                                    groupTranslations['first_name'],
                                [`middle_names_${lang.id}`]:
                                    groupTranslations['middle_names'],
                                [`last_name_${lang.id}`]:
                                    groupTranslations['last_name'],
                            }
                        })

                        return true
                    })
                }

                if (isViewOrEdit) {
                    setAllowRemoveImage(
                        response.data.data.user.media_store_id !== null
                    )
                    setPreviewSrc(
                        response.data.data.user.media_store_id
                            ? `${process.env.REACT_APP_API_ROOT_URL}/media/download/${response.data.data.user.media_store_id}`
                            : ''
                    )
                } else {
                    setPreviewSrc('')
                }
            } else {
                throw Error(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            setIsLoading(false)
            handleAxiosErrors(err, setErrMsg, setErrorFields)
        }
    }, [axiosPrivate, id, initData.languages, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        record.remove_user_image =
            !record.user_image &&
            record.media_store_id !== null &&
            !allowRemoveImage

        try {
            const routePath = isViewOrEdit
                ? '/administration/users/' + id
                : '/administration/users/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields, config)
        }
    }

    const hideDialog = () => {
        setUserDialog(false)
        navigate('/administration/users')
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/administration/users/delete/' + id}
                    record={user}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete this user?
                        </p>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'user-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={UserFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    return (
                        <FormikForm id="user-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col lg={9}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={12}>
                                                <Row>
                                                    <Col xl={6}>
                                                        <TextField
                                                            id="formUserUsername"
                                                            name="username"
                                                            label="Username"
                                                            placeholder="Enter user's username"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            required
                                                        />
                                                    </Col>
                                                    <Col xl={6}>
                                                        <TextField
                                                            id="formUserEmail"
                                                            name="email"
                                                            label="Email Address"
                                                            placeholder="Enter user's email"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            required
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col xl={12}>
                                                <Row>
                                                    <Col xl={6}>
                                                        <SelectField
                                                            id="formUserPersonTitlesId"
                                                            name="titles_id"
                                                            label="Title"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            choices={titles}
                                                        />
                                                    </Col>
                                                    <Col xl={6}>
                                                        <DateField
                                                            id="formUserPersonDateOfBirth"
                                                            name="date_of_birth"
                                                            label="Date of birth"
                                                            placeholder="DD/MM/YYYY"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col xl={12}>
                                                <RenderTranslatedFields
                                                    id="user"
                                                    formikProps={formikProps}
                                                    fields={[
                                                        {
                                                            id: 'formUserFirstName',
                                                            type: 'text',
                                                            name: 'first_name',
                                                            label: 'First Name',
                                                            placeholder:
                                                                'Enter first name',
                                                            required: true,
                                                        },
                                                        {
                                                            id: 'formUserMiddleNames',
                                                            type: 'text',
                                                            name: 'middle_names',
                                                            label: 'Middle Name(s)',
                                                            placeholder:
                                                                'Enter middle name(s)',
                                                        },
                                                        {
                                                            id: 'formUserLastName',
                                                            type: 'text',
                                                            name: 'last_name',
                                                            label: 'Last Name',
                                                            placeholder:
                                                                'Enter last name',
                                                            required: true,
                                                        },
                                                    ]}
                                                />
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                                <Col lg={3}>
                                    <fieldset>
                                        <legend>
                                            Image{' '}
                                            <HintMessage
                                                message={
                                                    <ImageReccomendedDimentionsMessage size="1024 x 1024 pixles" />
                                                }
                                            />
                                        </legend>
                                        <ImageField
                                            id="userImageFile"
                                            name="user_image"
                                            label="Image"
                                            formikProps={formikProps}
                                            imageId={user.media_store_id}
                                            alt={`${user.first_name} image`}
                                            allowRemoveImage={allowRemoveImage}
                                            setAllowRemoveImage={
                                                setAllowRemoveImage
                                            }
                                            previewSrc={previewSrc}
                                            setPreviewSrc={setPreviewSrc}
                                            updateRecord={setUser}
                                        />
                                    </fieldset>
                                </Col>
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>Security Roles</legend>
                                        <Row>
                                            <Col xl={12}>
                                                <CheckboxField
                                                    id="formUserRoleUser"
                                                    name="roles"
                                                    value={ROLES.User}
                                                    label="User"
                                                    formikProps={formikProps}
                                                />
                                                <CheckboxField
                                                    id="formUserRoleEditor"
                                                    name="roles"
                                                    value={ROLES.Editor}
                                                    label="Editor"
                                                    formikProps={formikProps}
                                                />
                                                <CheckboxField
                                                    id="formUserRoleAdmin"
                                                    name="roles"
                                                    value={ROLES.Admin}
                                                    label="Administrator"
                                                    formikProps={formikProps}
                                                />
                                                <CheckboxField
                                                    id="formUserRoleOwner"
                                                    name="roles"
                                                    value={ROLES.Owner}
                                                    label="Project Owner"
                                                    formikProps={formikProps}
                                                />
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                            </Row>
                            <input
                                type="submit"
                                id="user-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={userDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '75vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : 'Edit user'
                                : 'Create new user'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : `Edit ${recordName}`
                                : 'Create new user'
                        }
                        buttons={[
                            {
                                variant: theme === 'dark' ? 'dark' : 'light',
                                label: 'Back to Users',
                                icon: 'fa-solid fa-arrow-left',
                                url: '/administration/users',
                            },
                        ]}
                    />
                    {FormContent}
                    <FormActions formFooter={formFooter} />
                </>
            )}
        </>
    )
}

export default UserForm
